import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { CheckSquare, FileText, Link2, Loader2, Pencil, Plus, Receipt, X } from "lucide-react";

const mdComponents = {
  a: ({ node, ...props }) => <a {...props} target="_blank" rel="noreferrer" className="text-amber-300 underline break-all" />,
  ul: ({ node, ...props }) => <ul {...props} className="list-disc pl-5 space-y-0.5" />,
  ol: ({ node, ...props }) => <ol {...props} className="list-decimal pl-5 space-y-0.5" />,
  p: ({ node, ...props }) => <p {...props} className="mb-1.5" />,
  h1: ({ node, ...props }) => <h4 {...props} className="font-semibold text-zinc-100 mt-2" />,
  h2: ({ node, ...props }) => <h4 {...props} className="font-semibold text-zinc-100 mt-2" />,
  h3: ({ node, ...props }) => <h4 {...props} className="font-semibold text-zinc-100 mt-2" />,
  code: ({ node, ...props }) => <code {...props} className="bg-zinc-800 px-1 rounded text-[11px]" />,
};

/** Descripción rica (Markdown básico, URLs clicables) · editable inline. */
export function LeadDescripcion({ lead, onSaved, readOnly }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(lead.descripcion_md || lead.notas || "");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/leads/${lead.id}`, { descripcion_md: val || null });
      setEditing(false); onSaved?.();
    } catch { toast.error("No se pudo guardar la descripción"); } finally { setSaving(false); }
  };
  const texto = lead.descripcion_md || lead.notas || "";
  return (
    <div className="bg-zinc-900/40 border border-zinc-800 rounded-lg p-4" data-testid="lead-descripcion">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-xs uppercase tracking-wider text-zinc-500 flex items-center gap-2"><FileText className="w-3.5 h-3.5" /> Descripción</h3>
        {!editing && !readOnly && (
          <Button size="sm" variant="ghost" onClick={() => { setVal(texto); setEditing(true); }} className="text-zinc-400 hover:text-amber-300 h-7" data-testid="lead-desc-edit">
            <Pencil className="w-3 h-3 mr-1" /> Editar
          </Button>
        )}
      </div>
      {editing ? (
        <div className="space-y-2">
          <Textarea value={val} onChange={(e) => setVal(e.target.value)} rows={8} autoFocus
            placeholder={"Qué quiere el cliente, medidas, plazos…\nSoporta **negrita**, listas con - y enlaces https://"}
            className="bg-zinc-950 border-zinc-800 text-sm font-mono" data-testid="lead-desc-input" />
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="outline" onClick={() => setEditing(false)} className="bg-transparent border-zinc-700 h-7">Cancelar</Button>
            <Button size="sm" onClick={save} disabled={saving} className="bg-amber-500 text-zinc-950 hover:bg-amber-400 h-7" data-testid="lead-desc-save">
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : "Guardar"}
            </Button>
          </div>
        </div>
      ) : texto ? (
        <div className="text-sm text-zinc-300 leading-relaxed" data-testid="lead-desc-render">
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>{texto}</ReactMarkdown>
        </div>
      ) : (
        <div className="text-xs text-zinc-600 italic">Sin descripción. Pulsa Editar para añadir contexto, enlaces y medidas.</div>
      )}
    </div>
  );
}

/** Checklist simple (subtareas) dentro del lead. */
export function LeadChecklist({ lead, onSaved, readOnly }) {
  const [nueva, setNueva] = useState("");
  const [saving, setSaving] = useState(false);
  const items = lead.subtareas || [];
  const persist = async (next) => {
    setSaving(true);
    try { await api.patch(`/leads/${lead.id}/subtareas`, { subtareas: next }); onSaved?.(); }
    catch { toast.error("No se pudo guardar la checklist"); } finally { setSaving(false); }
  };
  const add = () => { if (!nueva.trim()) return; persist([...items, { titulo: nueva.trim(), hecho: false }]); setNueva(""); };
  const hechas = items.filter((s) => s.hecho).length;
  return (
    <div className="bg-zinc-900/40 border border-zinc-800 rounded-lg p-4" data-testid="lead-checklist">
      <h3 className="text-xs uppercase tracking-wider text-zinc-500 flex items-center gap-2 mb-2">
        <CheckSquare className="w-3.5 h-3.5" /> Checklist {items.length > 0 && <span className="text-zinc-600 normal-case">({hechas}/{items.length})</span>}
        {saving && <Loader2 className="w-3 h-3 animate-spin" />}
      </h3>
      <div className="space-y-1">
        {items.map((s) => (
          <label key={s.id} className="flex items-start gap-2 text-sm group min-h-[32px]" data-testid={`subtarea-${s.id}`}>
            <input type="checkbox" checked={!!s.hecho} disabled={readOnly} className="accent-emerald-500 mt-1"
              onChange={() => persist(items.map((x) => x.id === s.id ? { ...x, hecho: !x.hecho } : x))} data-testid={`subtarea-check-${s.id}`} />
            <span className={`flex-1 ${s.hecho ? "line-through text-zinc-500" : "text-zinc-200"}`}>
              {s.titulo}
              {s.hecho && s.hecho_por && <span className="text-[10px] text-zinc-600 ml-2">✓ {s.hecho_por} · {(s.hecho_at || "").slice(0, 10)}</span>}
            </span>
            {!readOnly && (
              <button type="button" onClick={() => persist(items.filter((x) => x.id !== s.id))} className="text-zinc-600 hover:text-red-400 opacity-0 group-hover:opacity-100" data-testid={`subtarea-del-${s.id}`}>
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </label>
        ))}
      </div>
      {!readOnly && (
        <div className="flex gap-2 mt-2">
          <Input value={nueva} onChange={(e) => setNueva(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()}
            placeholder="Añadir paso… (Enter)" className="bg-zinc-950 border-zinc-800 h-8 text-sm" data-testid="subtarea-input" />
          <Button size="sm" onClick={add} disabled={!nueva.trim()} className="bg-zinc-800 hover:bg-zinc-700 h-8" data-testid="subtarea-add"><Plus className="w-3.5 h-3.5" /></Button>
        </div>
      )}
    </div>
  );
}

/** Añadir URL de referencia a la bolsa de archivos (reutiliza /leads/{id}/archivos con tipo "url"). */
export function LeadAddUrl({ leadId, onSaved }) {
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState("");
  const [nombre, setNombre] = useState("");
  const save = async () => {
    if (!/^https?:\/\//i.test(url.trim())) { toast.error("La URL debe empezar por http:// o https://"); return; }
    try {
      await api.post(`/leads/${leadId}/archivos`, { nombre: nombre.trim() || url.trim(), url: url.trim(), tipo: "url" });
      setOpen(false); setUrl(""); setNombre(""); onSaved?.();
    } catch { toast.error("No se pudo guardar el enlace"); }
  };
  if (!open) return (
    <Button size="sm" variant="outline" onClick={() => setOpen(true)} className="bg-transparent border-zinc-700 h-7 text-xs" data-testid="lead-add-url-btn">
      <Link2 className="w-3.5 h-3.5 mr-1" /> Enlace
    </Button>
  );
  return (
    <div className="flex items-center gap-1">
      <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://…" className="bg-zinc-950 border-zinc-800 h-7 text-xs w-44" data-testid="lead-url-input" autoFocus />
      <Input value={nombre} onChange={(e) => setNombre(e.target.value)} placeholder="Nombre (opcional)" className="bg-zinc-950 border-zinc-800 h-7 text-xs w-32" data-testid="lead-url-nombre" />
      <Button size="sm" onClick={save} className="bg-amber-500 text-zinc-950 hover:bg-amber-400 h-7 text-xs" data-testid="lead-url-save">OK</Button>
      <button type="button" onClick={() => setOpen(false)} className="text-zinc-500 hover:text-zinc-200"><X className="w-4 h-4" /></button>
    </div>
  );
}

const PRES_ESTADO = { borrador: "text-zinc-300 border-zinc-700", enviado: "text-sky-200 border-sky-500/40", aceptado: "text-emerald-200 border-emerald-500/40",
  rechazado: "text-red-200 border-red-500/40", caducado: "text-zinc-400 border-zinc-700", anulado_por_cliente: "text-fuchsia-200 border-fuchsia-500/40" };

/** Presupuestos vinculados + botón «Crear presupuesto». */
export function LeadPresupuestos({ lead, onSaved }) {
  const nav = useNavigate();
  const [creating, setCreating] = useState(false);
  const pres = lead.presupuestos || [];
  const crear = async () => {
    setCreating(true);
    try {
      const { data } = await api.post(`/leads/${lead.id}/presupuesto`, {});
      toast.success(`Presupuesto ${data.numero} creado${data.con_cliente ? "" : " (sin cliente: se asigna en el presupuesto)"}`);
      onSaved?.();
      nav(`/presupuestos/${data.presupuesto_id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo crear el presupuesto"); } finally { setCreating(false); }
  };
  const fmt = (n) => new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(n || 0);
  return (
    <div className="bg-zinc-900/40 border border-zinc-800 rounded-lg p-4" data-testid="lead-presupuestos">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-xs uppercase tracking-wider text-zinc-500 flex items-center gap-2"><Receipt className="w-3.5 h-3.5" /> Presupuestos vinculados ({pres.length})</h3>
        <Button size="sm" onClick={crear} disabled={creating} className="bg-amber-500 text-zinc-950 hover:bg-amber-400 h-7 text-xs" data-testid="lead-crear-presupuesto-btn">
          {creating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Plus className="w-3.5 h-3.5 mr-1" /> Crear presupuesto</>}
        </Button>
      </div>
      {lead.cliente_id || lead.cliente_existente_id ? (
        <div className="text-[10px] text-zinc-500 mb-2">Se reutiliza el cliente <b className="text-zinc-300">{lead.cliente_nombre || lead.cliente_existente_nombre}</b> (no se duplica).</div>
      ) : (
        <div className="text-[10px] text-zinc-500 mb-2">Sin cliente vinculado: el presupuesto usará los datos del lead y podrás elegir/crear el cliente en la ficha del presupuesto.</div>
      )}
      {lead.resumen_comercial && lead.resumen_comercial.n_partidas > 0 && (
        <div className="grid grid-cols-3 gap-2 mb-3 text-center" data-testid="lead-resumen-comercial">
          {[["Presupuestado", lead.resumen_comercial.presupuestado, "text-zinc-100"], ["Ganado", lead.resumen_comercial.convertido, "text-emerald-300"], ["Pendiente", lead.resumen_comercial.pendiente, "text-amber-200"]].map(([l, v, c]) => (
            <div key={l} className="bg-zinc-950/60 border border-zinc-800 rounded-md px-2 py-1.5">
              <div className="text-[9px] uppercase tracking-wider text-zinc-500">{l}</div>
              <div className={`text-sm font-semibold ${c}`}>{fmt(v)}</div>
            </div>
          ))}
          <div className="col-span-3 text-[10px] text-zinc-500">
            <span className="text-zinc-600">Importes base imponible (sin IVA) · </span>
            {lead.resumen_comercial.n_convertidas}/{lead.resumen_comercial.n_partidas} partidas en taller
            {lead.resumen_comercial.pospuesto > 0 && ` · pospuesto ${fmt(lead.resumen_comercial.pospuesto)}`}
            {lead.resumen_comercial.rechazado > 0 && ` · rechazado ${fmt(lead.resumen_comercial.rechazado)}`}
            {(lead.resumen_comercial.peticiones || []).length > 0 && <> · {lead.resumen_comercial.peticiones.map((x) => (
              <button key={x.id} type="button" onClick={() => nav(`/peticiones/${x.id}`)} className="font-mono text-amber-300 hover:underline ml-1" data-testid={`lead-pet-${x.id}`}>{x.codigo}</button>
            ))}</>}
          </div>
        </div>
      )}
      {pres.length === 0 ? <div className="text-xs text-zinc-600 italic">Ningún presupuesto todavía.</div> : (
        <div className="space-y-1">
          {pres.map((p) => (
            <button key={p.id} type="button" onClick={() => nav(`/presupuestos/${p.id}`)} data-testid={`lead-pres-${p.id}`}
              className="w-full flex items-center justify-between gap-2 px-2 py-1.5 rounded border border-zinc-800 hover:border-amber-500/40 hover:bg-zinc-900 text-left text-sm min-h-[36px]">
              <span className="font-mono text-amber-300">{p.numero}</span>
              <span className={`text-[10px] px-1.5 py-0.5 rounded border ${PRES_ESTADO[p.estado] || PRES_ESTADO.borrador}`}>{p.estado}</span>
              <span className="text-zinc-400 text-xs ml-auto">{p.fecha} · {fmt(p.total)}</span>
              {p.partidas?.convertidas > 0 && <span className="text-[10px] text-violet-300">{p.partidas.convertidas}/{p.partidas.total} en taller</span>}
              {p.peticion_id && <span className="text-[10px] text-emerald-300">→ petición</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
